import React from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Box, Text, HStack, VStack, Badge, Button, Icon, UnorderedList, ListItem, Divider,
} from "@chakra-ui/react";
import {
  Target, Zap, Users, FileText, Radio, MessageCircle, AlertTriangle, CheckCircle2, Lightbulb,
} from "lucide-react";

const ACCENT = "#F68633";

/** Um bloco do manual: ícone, título e o conteúdo (texto ou lista de cuidados). */
function Secao({ icone, titulo, texto, itens, cor = "gray.700", destaque = false }) {
  if (!texto && !(itens && itens.length)) return null;
  return (
    <Box
      borderWidth={destaque ? "1px" : 0}
      borderColor="orange.200"
      bg={destaque ? "orange.50" : "transparent"}
      borderRadius="lg"
      px={destaque ? 3 : 0}
      py={destaque ? 2.5 : 0}
    >
      <HStack spacing={2} mb={1} align="center">
        <Icon as={icone} boxSize={3.5} color={destaque ? ACCENT : "gray.400"} />
        <Text fontSize="xs" fontWeight="bold" color={destaque ? "orange.700" : "gray.500"} textTransform="uppercase" letterSpacing="0.04em">
          {titulo}
        </Text>
      </HStack>
      {texto && <Text fontSize="sm" color={cor}>{texto}</Text>}
      {itens?.length > 0 && (
        <UnorderedList spacing={1} pl={4} mt={texto ? 1 : 0}>
          {itens.map((c, i) => (
            <ListItem key={i}><Text fontSize="sm" color={cor}>{c}</Text></ListItem>
          ))}
        </UnorderedList>
      )}
    </Box>
  );
}

/**
 * Manual de execução da tarefa (V1.7.1). Traz para dentro do CRM o "como usar"
 * que só existia no documento das caixas de tarefas: objetivo, canal, abordagem,
 * cuidados e exemplo. O conteúdo vem do catálogo do back-end (`catalogo` do board),
 * então documento e tela nunca divergem.
 */
export default function ComoUsarTarefaModal({ isOpen, onClose, info, tarefa, onRegistrarDesfecho }) {
  if (!info) return null;

  const cuidados = Array.isArray(info.cuidados) ? info.cuidados : (info.cuidados ? [info.cuidados] : []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" pb={2}>
          <HStack spacing={2} flexWrap="wrap">
            <Badge colorScheme="purple" variant="subtle">{info.codigo}</Badge>
            <Text fontSize="md">{info.nome}</Text>
          </HStack>
          <Text fontSize="xs" fontWeight="normal" color="gray.500" mt={1}>Como fazer esta tarefa</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack align="stretch" spacing={3.5}>
            {tarefa?.cliente_nome && (
              <Box bg="gray.50" borderRadius="md" px={3} py={2}>
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{tarefa.cliente_nome}</Text>
                {tarefa.motivo && <Text fontSize="xs" color="gray.500" noOfLines={2}>{tarefa.motivo}</Text>}
              </Box>
            )}

            <Secao icone={Target} titulo="Objetivo" texto={info.objetivo || info.descricao} destaque />
            <Secao icone={Radio} titulo="Canal" texto={info.canal} />
            <Secao icone={MessageCircle} titulo="Abordagem" texto={info.abordagem} />
            <Secao icone={AlertTriangle} titulo="Cuidados" itens={cuidados} cor="red.700" />

            <Divider />

            <Secao icone={Zap} titulo="Por que esta tarefa apareceu" texto={info.gatilho} />
            <Secao icone={Users} titulo="Quem entra" texto={info.elegibilidade} />
            <Secao icone={FileText} titulo="O que você já tem em mãos" texto={info.contexto} />
            <Secao icone={CheckCircle2} titulo="O que conta como cumprido" texto={info.cumprido} />

            {info.exemplo && (
              <Box bg="gray.50" borderRadius="lg" px={3} py={2.5}>
                <HStack spacing={2} mb={1}>
                  <Icon as={Lightbulb} boxSize={3.5} color="gray.400" />
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                    Exemplo
                  </Text>
                </HStack>
                <Text fontSize="sm" color="gray.600" fontStyle="italic">{info.exemplo}</Text>
              </Box>
            )}

            <Text fontSize="xs" color="gray.400">
              Depois do toque, registre o desfecho nos três eixos (esforço, contato e objetivo):
              é ele que fecha a tarefa, conta nas suas metas e encadeia a próxima.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Fechar</Button>
          {onRegistrarDesfecho && (
            <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#d4702a" }} onClick={onRegistrarDesfecho}>
              Registrar desfecho
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
